import { setSelectors } from './utilities.js';
import { request } from './cart-service.js';

class RcDiscountApplier extends HTMLElement {

  constructor() {
    super();
    this.events = {};
    this.selectors = {};
    this._submitting = false;
    this._onSubmit = this._onSubmit.bind(this);
  };

  connectedCallback() { this.initialize(); };

  disconnectedCallback() { this.deinitialize(); };

  initialize(){
    const U = window.umbrellaConfig || {};
    this.events = U.events || {};

    this.selectors = setSelectors(this, {
      form: '#apply-voucher-form',
      input: '#discount-input',
      feedback: '#discount-feedback',
    });

    if(this.selectors.form){
      this.selectors.form.addEventListener('submit', this._onSubmit);
    };
  };

  deinitialize(){
    if(this.selectors && this.selectors.form){
      this.selectors.form.removeEventListener('submit', this._onSubmit);
    };
  };

  _showFeedback(message, type = 'info'){
    const feedback = this.selectors.feedback;

    showToast(message, type);

    // Inline feedback as visual fallback
    if(feedback){
      feedback.textContent = message;
      feedback.classList.remove('hidden');
      feedback.classList.remove('text-green-600', 'text-red-600');
      if(type === 'success') feedback.classList.add('text-green-600');
      else if(type === 'error') feedback.classList.add('text-red-600');
      feedback.setAttribute('aria-live', 'polite');
    };
  };

  _emitEvents(code, cart){
    const U = window.umbrellaConfig || {};
    const events = this.events || {};
    const names = new Set();
    const discountEvt = events.discountApplied || 'discount:applied';
    const cartChangeEvt = events.cartChange || 'cart:state';

    if(discountEvt) names.add(discountEvt);
    names.add('discount:applied');

    if(cartChangeEvt) names.add(cartChangeEvt);
    names.add('cart:state');

    names.forEach((evtName) => {
      document.dispatchEvent(new CustomEvent(evtName, {
        detail: { code, cart, source: 'discount' },
        bubbles: true,
      }));
    });

    if(typeof U.syncCart === 'function'){
      try { U.syncCart(); } catch(_) { /* noop */ };
    };
  };

  async _onSubmit(event){
    event.preventDefault();
    if(this._submitting) return;

    const U = window.umbrellaConfig || {};
    const input = this.selectors.input;
    const code = input && typeof input.value === 'string' ? input.value.trim() : '';

    if(!code){
      this._showFeedback(U.discountEmptyMessage || 'Please enter a discount code.', 'error');
      return;
    };

    this._submitting = true;
    if(input) input.setAttribute('disabled', 'disabled');

    try {
      // If this code is already applied, avoid redundant requests and give clearer feedback
      try {
        const preCart = await request(window.Shopify.routes.root + 'cart.js');
        const preApps = Array.isArray(preCart.cart_level_discount_applications) ? preCart.cart_level_discount_applications : [];
        const alreadyApplied = preApps.some(d => {
          const title = (d && d.title) ? String(d.title).toLowerCase() : '';
          return title.includes(code.toLowerCase());
        });
        if(alreadyApplied){
          this._showFeedback(U.discountAlreadyAppliedMessage || 'This discount is already applied.', 'info');
          return;
        };
      } catch(_) {
        // If pre-check fails, continue with best-effort apply
      };

      await request(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        body: JSON.stringify({ discount: code })
      });

      const cart = await request(window.Shopify.routes.root + 'cart.js');
      const applications = Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : [];
      const isApplied = applications.some(d => {
        const title = (d && d.title) ? String(d.title).toLowerCase() : '';
        return title.includes(code.toLowerCase());
      });
      if(isApplied || applications.length > 0){
        this._showFeedback(U.discountAppliedMessage || 'Discount applied!', 'success');
        this._emitEvents(code, cart);
      } else {
        this._showFeedback(U.discountInvalidMessage || 'Invalid or ineligible code.', 'error');
      };
    } catch(error){
      if(U.devMode) console.error('Discount error:', error);
      this._showFeedback(U.discountErrorMessage || 'Unable to apply discount code.', 'error');
    } finally {
      this._submitting = false;
      if(input) input.removeAttribute('disabled');
    };
  };
};

if(!customElements.get('rc-discount-applier')) customElements.define('rc-discount-applier', RcDiscountApplier);